import { useEffect, useRef, useState, type FC } from 'react'
import type { GridEditorProps } from '../types'
import classes from '../grid-theme.module.scss'

/**
 * Text cell editor. Enter commits, Escape reverts, blur commits — the same
 * contract ag-grid's `stopEditingWhenCellsLoseFocus` gave the config grids.
 *
 * Teardown commits too. A control that swallows `mousedown` to protect what is
 * underneath it (the Hydraulic "+ Add Sub Pipe" strip) stops the input ever
 * blurring, so a page calling `api.stopEditing()` would unmount the editor with
 * the typed value still in it and silently lose the edit.
 */
const TextEditor: FC<GridEditorProps<unknown>> = ({
  value,
  params,
  onCommit,
  onCancel,
}) => {
  const [draft, setDraft] = useState(value == null ? '' : String(value))
  const inputRef = useRef<HTMLInputElement>(null)
  // Escape must not let the blur handler commit the value on the way out.
  const cancelledRef = useRef(false)
  // Whichever path commits first wins; the rest become no-ops, so a blur
  // followed by unmount cannot commit the same edit twice.
  const settledRef = useRef(false)
  const draftRef = useRef(draft)
  draftRef.current = draft
  const onCommitRef = useRef(onCommit)
  onCommitRef.current = onCommit

  const commit = (next: string) => {
    if (settledRef.current || cancelledRef.current) return
    settledRef.current = true
    onCommitRef.current(next)
  }

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()

    return () => {
      if (settledRef.current || cancelledRef.current) return
      settledRef.current = true
      onCommitRef.current(draftRef.current)
    }
  }, [])

  const maxLength = typeof params?.maxLength === 'number' ? params.maxLength : undefined

  return (
    <input
      ref={inputRef}
      className={classes.editorInput}
      value={draft}
      maxLength={maxLength}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => commit(draft)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit(draft)
        } else if (e.key === 'Escape') {
          e.preventDefault()
          cancelledRef.current = true
          onCancel()
        } else if (e.key === 'Tab') {
          commit(draft)
        }
      }}
    />
  )
}

export default TextEditor
